/**
 * Billing and time tracking utilities for calculating billable units.
 * Billable units are 15-minute increments with an 8-minute threshold rule:
 * - Sessions lasting at least 8 minutes into the next unit round up
 * - Sessions lasting under 8 minutes into the next unit round down
 */

export const ONE_UNIT_IN_SECONDS = 15 * 60; // 15 minutes
export const BILLING_THRESHOLD_IN_SECONDS = 8 * 60; // 8 minutes

/**
 * Calculates the number of billable units based on elapsed time.
 * Uses the 8-minute threshold rule for rounding.
 *
 * @param elapsedSeconds Total elapsed seconds
 * @returns Number of billable units
 *
 * @example
 * calculateBillableUnits(0) // 0
 * calculateBillableUnits(600) // 0 (10 minutes, under threshold)
 * calculateBillableUnits(900) // 1 (15 minutes exactly)
 * calculateBillableUnits(1380) // 2 (23 minutes, 8 min into next unit, rounds up)
 */
export function calculateBillableUnits(elapsedSeconds: number): number {
  if (elapsedSeconds < 0) return 0;

  const wholeUnits = Math.floor(elapsedSeconds / ONE_UNIT_IN_SECONDS);
  const secondsIntoCurrentUnit = elapsedSeconds % ONE_UNIT_IN_SECONDS;

  // Add one more unit if we're 8+ minutes into the current unit
  return (
    wholeUnits +
    (secondsIntoCurrentUnit >= BILLING_THRESHOLD_IN_SECONDS ? 1 : 0)
  );
}

/**
 * Calculates seconds remaining until the next billable unit is earned.
 * This is useful for displaying a countdown timer.
 *
 * @param elapsedSeconds Total elapsed seconds
 * @returns Seconds until next billable unit
 *
 * @example
 * calculateSecondsUntilNextUnit(0) // 480 (8 minutes to first unit)
 * calculateSecondsUntilNextUnit(480) // 420 (7 minutes more to first unit)
 * calculateSecondsUntilNextUnit(900) // 480 (just earned first unit, 8 min to second)
 */
export function calculateSecondsUntilNextUnit(elapsedSeconds: number): number {
  if (elapsedSeconds < 0) return BILLING_THRESHOLD_IN_SECONDS;

  const secondsIntoCurrentUnit = elapsedSeconds % ONE_UNIT_IN_SECONDS;

  if (secondsIntoCurrentUnit >= BILLING_THRESHOLD_IN_SECONDS) {
    // Already earned this unit, countdown to 8 minutes into next unit
    return (
      ONE_UNIT_IN_SECONDS +
      BILLING_THRESHOLD_IN_SECONDS -
      secondsIntoCurrentUnit
    );
  }

  // Not yet earned this unit, countdown to 8 minutes into current unit
  return BILLING_THRESHOLD_IN_SECONDS - secondsIntoCurrentUnit;
}

/**
 * Formats seconds as a MM:SS string.
 *
 * @param totalSeconds Total seconds to format
 * @returns Formatted time string (e.g., "15:00", "7:32")
 *
 * @example
 * formatTimeAsMMSS(0) // "0:00"
 * formatTimeAsMMSS(60) // "1:00"
 * formatTimeAsMMSS(452) // "7:32"
 * formatTimeAsMMSS(900) // "15:00"
 */
export function formatTimeAsMMSS(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${`0${seconds}`.slice(-2)}`;
}

/**
 * Helper to get the plural suffix for a word.
 *
 * @param count The count to check
 * @returns "s" if count !== 1, empty string otherwise
 *
 * @example
 * getPluralSuffix(0) // "s"
 * getPluralSuffix(1) // ""
 * getPluralSuffix(2) // "s"
 */
export function getPluralSuffix(count: number): string {
  return count !== 1 ? 's' : '';
}

/**
 * Complete billing metrics for a session.
 */
export interface BillingMetrics {
  billableUnits: number;
  totalMinutes: number;
  secondsUntilNextUnit: number;
  formattedNextUnitTime: string;
  minutesUnitLabel: string;
  unitsLabel: string;
}

/**
 * Calculates all billing metrics in a single call.
 * This combines all calculations for convenience.
 *
 * @param elapsedSeconds Total elapsed seconds
 * @returns Object containing all billing metrics
 *
 * @example
 * const metrics = calculateBillingMetrics(600);
 * // {
 * //   billableUnits: 0,
 * //   totalMinutes: 10,
 * //   secondsUntilNextUnit: 300,
 * //   formattedNextUnitTime: "5:00",
 * //   minutesUnitLabel: "mins",
 * //   unitsLabel: "unit"
 * // }
 */
export function calculateBillingMetrics(
  elapsedSeconds: number,
): BillingMetrics {
  const billableUnits = calculateBillableUnits(elapsedSeconds);
  const totalMinutes = Math.floor(elapsedSeconds / 60);
  const secondsUntilNextUnit = calculateSecondsUntilNextUnit(elapsedSeconds);
  const formattedNextUnitTime = formatTimeAsMMSS(secondsUntilNextUnit);

  return {
    billableUnits,
    totalMinutes,
    secondsUntilNextUnit,
    formattedNextUnitTime,
    minutesUnitLabel: `min${getPluralSuffix(totalMinutes)}`,
    unitsLabel: `unit${getPluralSuffix(billableUnits)}`,
  };
}
